class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.form = this.querySelector("form.search") || this.querySelector("form");
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search]"
    );
    this.isMobileSheetInstance = this.classList.contains("mobile-search-sheet__predictive");
    this.mobileSearchContent = this.querySelector("[data-mobile-search-content]");
    this.mobileSearchDiscover = this.querySelector("[data-mobile-search-discover]");
    this.mobileSearchShortcuts = this.querySelector("[data-mobile-search-shortcuts]");
    this.resultsLimit = this.isMobileSheetInstance ? 6 : 4;
    this.handleViewportChange = this.syncOpenResultsMaxHeight.bind(this);

    if (!this.form || !this.input || !this.predictiveSearchResults) {
      return;
    }

    this.setupEventListeners();
    if (this.isMobileSheetInstance) {
      this.setMobileSheetMode(this.getQuery().length ? "results" : "discover");
    }
  }

  setupEventListeners() {
    this.form.addEventListener("submit", this.onFormSubmit.bind(this));

    this.input.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this)
    );
    this.input.addEventListener("focus", this.onFocus.bind(this));

    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));

    if (this.isMobileSheetInstance) {
      window.addEventListener("resize", this.handleViewportChange);
      if (window.visualViewport) {
        window.visualViewport.addEventListener("resize", this.handleViewportChange);
        window.visualViewport.addEventListener("scroll", this.handleViewportChange);
      }
    }
  }

  disconnectedCallback() {
    if (!this.isMobileSheetInstance) return;
    window.removeEventListener("resize", this.handleViewportChange);
    if (window.visualViewport) {
      window.visualViewport.removeEventListener("resize", this.handleViewportChange);
      window.visualViewport.removeEventListener("scroll", this.handleViewportChange);
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    )
      event.preventDefault();
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) return;

    if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) return;

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll("li");
    let activeElement = this.querySelector("li");

    if (moveUp && !selectedElement) return;

    if (this.statusElement) this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement =
        selectedElement.previousElementSibling ||
        allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector(
      '[aria-selected="true"] a, [aria-selected="true"] button'
    );

    if (selectedProduct) selectedProduct.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/\s+/g, "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(
        searchTerm
      )}&${encodeURIComponent("resources[type]")}=product&${encodeURIComponent(
        "resources[limit]"
      )}=${this.resultsLimit}&section_id=predictive-search`
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkupContainer = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search");
        if (!resultsMarkupContainer) {
          this.close();
          return;
        }
        const resultsMarkup = resultsMarkupContainer.innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    if (!this.statusElement) return;

    // Keep the discover panel visible until we actually have rendered results.
    if (this.isMobileSheetInstance && this.getAttribute("results") !== "true") {
      this.setMobileSheetMode("discover");
    }

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    if (!this.predictiveSearchResults) return;
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    const liveRegionCount = this.querySelector(
      "[data-predictive-search-live-region-count-value]"
    );
    if (liveRegionCount) {
      this.setLiveRegionText(liveRegionCount.textContent);
    }
  }

  getResultsMaxHeight() {
    const sectionGet =
      document.getElementById("shopify-section-header") ||
      document.getElementById("shopify-section-header-v2");
    const sectionBottom = sectionGet
      ? sectionGet.getBoundingClientRect().bottom
      : 0;
    this.resultsMaxHeight = Math.max(window.innerHeight - sectionBottom, 220);
    return this.resultsMaxHeight;
  }

  getMobileResultsMaxHeight() {
    const anchor = this.mobileSearchContent || this.predictiveSearchResults;
    if (!anchor) return 220;
    const viewportHeight = window.visualViewport
      ? window.visualViewport.height + window.visualViewport.offsetTop
      : window.innerHeight;
    const rect = anchor.getBoundingClientRect();
    const availableHeight = Math.floor(viewportHeight - rect.top - 10);
    return Math.max(availableHeight, 180);
  }

  syncOpenResultsMaxHeight() {
    if (!this.hasAttribute("open")) return;
    this.open();
  }

  open() {
    if (!this.predictiveSearchResults || !this.input) return;
    if (this.isMobileSheetInstance) {
      this.setMobileSheetMode("results");
    }
    const maxHeight = this.isMobileSheetInstance
      ? this.getMobileResultsMaxHeight()
      : this.resultsMaxHeight || this.getResultsMaxHeight();
    const minimumHeight = this.isMobileSheetInstance ? 96 : 180;
    this.predictiveSearchResults.style.maxHeight = `${Math.max(minimumHeight, maxHeight)}px`;
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      if (this.input) this.input.value = "";
      this.removeAttribute("results");
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute("aria-selected", false);

    if (this.input) this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("open");
    if (this.input) this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.removeAttribute("style");
    }

    if (this.isMobileSheetInstance) {
      this.setMobileSheetMode(this.getQuery().length ? "results" : "discover");
    }
  }

  setMobileSheetMode(mode) {
    if (!this.isMobileSheetInstance) return;

    const showResults = mode === "results";
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.hidden = !showResults;
    }
    if (this.mobileSearchDiscover) {
      this.mobileSearchDiscover.hidden = showResults;
    }
    if (this.mobileSearchShortcuts) {
      this.mobileSearchShortcuts.hidden = showResults;
    }
  }
}

customElements.define("predictive-search", PredictiveSearch);
